import * as SVG from '../svg/svg';
import { CubicBezier } from '../svg/bezier';
import { closePoints, greaterThen } from '../svg/line';
import { Line } from '../svg/line';
import { Path } from '../svg/path';
import { Polygon } from '../geometry/polygon';
import { Datum, TransverseMercator, Mercator } from '../map/projection';
import { LatLng, Point2D } from '../map/map-types';
import { MapOverlaySVGResult } from '../map/svg-map-overlay-result';
import { markup2element } from '../dom/dom';
import { SetSweeper } from '../webgl/set-sweeper';

interface HysplitScalar {
	long_name: string,
	standard_name: string,
	units: string,
	values: number[][]
}

export interface HysplitResult {
	bbox: [number, number, number, number], //west, south, east, north
	features: {
		AIR_TEMP: HysplitScalar,
		H2OMIXRA: HysplitScalar,
		MIXDEPTH: HysplitScalar,
		PRESSURE: HysplitScalar,
		RAINFALL: HysplitScalar,
		RELHUMID: HysplitScalar,
		SPCHUMID: HysplitScalar,
		SUN_FLUX: HysplitScalar,
		TERR_MSL: HysplitScalar,
		THETA: HysplitScalar,
		hgt: HysplitScalar,
		time: HysplitScalar,
		geometry: {
			type: "MultiLineString",
			coordinates: [number, number, number][][]
		},
		properties: {
			direction: "BACKWARD" | "FOREWARD",
			"i-hgt": number,
			"i-lat": number,
			"i-lon": number,
			"i-time": number,
			runtime: number,
			"top-of-model": number,
			uuid: string,
			"vertical-motion": string,
			t0Millis: number,
			tnMillis: number,
			trajectoryCount: number
		},
		type: "Feature"

	}[],
	type: "FeatureCollection"
}


export class Hysplit {
	private trajectories: Trajectory[];
	private ensemble: Ensemble;
	public options: HysplitOptions;
	public properties: HysplitProperties;

	constructor(hysplitResult: HysplitResult, options?: HysplitOptions, translate?: { dLat: number, dLng: number }, contourStartWidth = 100.0, contourEndWidth = 750.0) {
		translate = translate ? translate : { dLat: 0.0, dLng: 0.0 };

		//            let iso8601StartTime = hysplitResult.features[0].properties['start-time'];
		let timeUnit = hysplitResult.features[0].time.units;
		let dateStartIndex = timeUnit.length - 19;
		let timeStartIndex = timeUnit.length - 8;

		let iso8601StartTime = timeUnit.substr(dateStartIndex, 10) + 'T' + timeUnit.substr(timeStartIndex, 10) + 'Z';
		//            let timestep = hysplitResult.features[0].properties['timestep'];
		let startLatitude = hysplitResult.features[0].properties['i-lat'];
		let startLongitude = hysplitResult.features[0].properties['i-lon'];

		let mapSize = Math.pow(2, 19);
		//          let timestepMillis = timestep * 60000;
		let trajectoryCount = hysplitResult.features[0].hgt.values.length;
		let t0Millis: number = new Date(iso8601StartTime).getTime();
		let tnMillis: number = t0Millis + hysplitResult.features[0].time.values[0][hysplitResult.features[0].time.values[0].length - 1] * 60000;
		hysplitResult.features[0].properties.t0Millis = t0Millis;
		hysplitResult.features[0].properties.tnMillis = tnMillis;
		hysplitResult.features[0].properties.trajectoryCount = trajectoryCount;

		this.trajectories = [];
		for (let i = 0; i < trajectoryCount; i++) {
			this.trajectories.push(new Trajectory(
				hysplitResult.features[0].geometry.coordinates[i],
				hysplitResult.features[0].hgt.values[i],
				t0Millis,
				hysplitResult.features[0].time.values[i],
				translate));
		}
		this.ensemble = undefined;
		try {
			this.ensemble = new Ensemble(hysplitResult.features[0].geometry.coordinates, contourStartWidth, contourEndWidth, translate, 0.1);
			if (!this.ensemble.contourPath) {
				this.ensemble = undefined;
			}
		} catch (error) {
			//error while finding contour. Needs reimplementation any way
			console.error(error);
		}
		this.properties = this.parseProperties(hysplitResult);
		this.setOptions(options);
	}
	private parseProperties(hysplitResult: HysplitResult): HysplitProperties {
		return {
			id: hysplitResult.features[0].properties['uuid'],
			result: hysplitResult.features[0].properties.trajectoryCount > 1 ? "ensemble" : "trajectory",
			direction: hysplitResult.features[0].properties.direction,
			runLength: ((hysplitResult.features[0].properties.tnMillis - hysplitResult.features[0].properties.t0Millis) / 3600000).toFixed(1),
			startTime: new Date(hysplitResult.features[0].properties.t0Millis).toLocaleString(),
			startHeight: hysplitResult.features[0].properties['i-hgt'].toFixed(1),
			startLocation: `${hysplitResult.features[0].properties['i-lon']} °E, ${hysplitResult.features[0].properties['i-lat']} °N`,
			trajectoryCount: hysplitResult.features[0].properties.trajectoryCount
		};
	}
	public setOptions(options?: HysplitOptions): Hysplit {
		this.fixOptions(options);

		for (let i = 0; i < this.trajectories.length; i++) {
			if (i === this.options.analyze || this.options.analyze >= this.trajectories.length) {
				this.trajectories[i].reset();
				this.trajectories[i].applyValueClassification(this.options.heightClasses);
			} else {
				this.trajectories[i].reset();
			}
		}

		return this;
	}
	private fixOptions(options?: HysplitOptions): void {
		this.options = {
			main: 0,
			analyze: -1,
			contourFill: {
				hue: 200,
				saturation: 0.11,
				lightness: 0.11,
				alpha: 0.5
			},
			contourStroke: {
				color: {
					hue: 200,
					saturation: 0.11,
					lightness: 0.11,
					alpha: 1.0
				},
				width: 1
			},
			mainStroke: {
				color: {
					hue: 247.0,
					saturation: 1.0,
					lightness: 0.41,
					alpha: 1.0
				},
				width: 2.0
			},
			baseStroke: {
				color: {
					hue: 67.0,
					saturation: 1.0,
					lightness: 0.41,
					alpha: 0.325
				},
				width: 0.75
			},
			heightClasses: undefined,
			trace: {
				map: {
					main: true,
					analyzed: true,
					alternatives: true
				},
				timeline: {
					main: true,
					analyzed: true,
					alternatives: false
				}
			},
			show: {
				map: {
					contour: true,
					main: true,
					analyzed: true,
					alternatives: true
				},
				timeline: {
					main: true,
					analyzed: true,
					alternatives: true
				}
			}
		};
		if (options) {
			this.options.main = options.main === undefined ? this.options.main : options.main < 0 ? 0 : options.main < this.trajectories.length ? options.main : this.trajectories.length - 1;
			this.options.analyze = options.heightClasses ?
				options.analyze === undefined ? this.options.main :
					options.analyze === 'main' ? this.options.main :
						options.analyze === 'all' ? this.trajectories.length :
							options.analyze < 0 ? -1 : options.analyze
				: -1;//no heightClasses
			this.options.contourFill = options.contourFill === undefined ? this.options.contourFill : options.contourFill;
			this.options.contourStroke = options.contourStroke === undefined ? this.options.contourStroke : options.contourStroke;
			this.options.mainStroke = options.mainStroke === undefined ? this.options.mainStroke : options.mainStroke;
			this.options.baseStroke = options.baseStroke === undefined ? this.options.baseStroke : options.baseStroke;
			this.options.heightClasses = options.heightClasses;

			this.options.analyze = this.options.heightClasses !== undefined && this.options.heightClasses.thresholds.length > 0 ? this.options.analyze : -1;

			if (options.trace) {
				if (options.trace.map) {
					this.options.trace.map.main = options.trace.map.main === undefined ? this.options.trace.map.main : options.trace.map.main;
					this.options.trace.map.analyzed = options.trace.map.analyzed === undefined ? this.options.trace.map.analyzed : options.trace.map.analyzed;
					this.options.trace.map.alternatives = options.trace.map.alternatives === undefined ? this.options.trace.map.alternatives : options.trace.map.alternatives;
				}
				if (options.trace.timeline) {
					this.options.trace.timeline.main = options.trace.timeline.main === undefined ? this.options.trace.timeline.main : options.trace.timeline.main;
					this.options.trace.timeline.analyzed = options.trace.timeline.analyzed === undefined ? this.options.trace.timeline.analyzed : options.trace.timeline.analyzed;
					this.options.trace.timeline.alternatives = options.trace.timeline.alternatives === undefined ? this.options.trace.timeline.alternatives : options.trace.timeline.alternatives;
				}
			}
			if (options.show) {
				if (options.show.map) {
					this.options.show.map.contour = this.ensemble !== undefined ? options.show.map.contour === undefined ? this.options.show.map.contour : options.show.map.contour : false;
					this.options.show.map.main = options.show.map.main === undefined ? this.options.show.map.main : options.show.map.main;
					this.options.show.map.analyzed = options.show.map.analyzed === undefined ? this.options.show.map.analyzed : options.show.map.analyzed;
					this.options.show.map.alternatives = options.show.map.alternatives === undefined ? this.options.show.map.alternatives : options.show.map.alternatives;
				}
				if (options.show.timeline) {
					this.options.show.timeline.main = options.show.timeline.main === undefined ? this.options.show.timeline.main : options.show.timeline.main;
					this.options.show.timeline.analyzed = options.show.timeline.analyzed === undefined ? this.options.show.timeline.analyzed : options.show.timeline.analyzed;
					this.options.show.timeline.alternatives = options.show.timeline.alternatives === undefined ? this.options.show.timeline.alternatives : options.show.timeline.alternatives;
				}
			}
		}
	}
	public getLatLngSVG(tracerTime?: number): MapOverlaySVGResult { //}HysplitLatLngSVG {
		let halfMapResolution = 0.5 * Renderer.MAP_RESOLUTION;
		let transform = (xy: number[]) => [xy[0] + halfMapResolution, halfMapResolution - xy[1]];


		let boundingBox: { yMax: number, yMin: number, xMax: number, xMin: number } = this.ensemble ? this.ensemble : { yMax: null, yMin: null, xMax: null, xMin: null };
		if (!this.ensemble) {
			for (let i = 0; i < this.trajectories.length; i++) {
				boundingBox.yMax = boundingBox.yMax !== null ? Math.max(this.trajectories[i].yMax, boundingBox.yMax) : this.trajectories[i].yMax;
				boundingBox.yMin = boundingBox.yMin !== null ? Math.min(this.trajectories[i].yMin, boundingBox.yMin) : this.trajectories[i].yMin;
				boundingBox.xMax = boundingBox.xMax !== null ? Math.max(this.trajectories[i].xMax, boundingBox.xMax) : this.trajectories[i].xMax;
				boundingBox.xMin = boundingBox.xMin !== null ? Math.min(this.trajectories[i].xMin, boundingBox.xMin) : this.trajectories[i].xMin;
			}
		}

		//calculate the viewBox
		//Due to the use of Bezier curves, the exact bounds based on the coordinates can be too tight.
		//So we widen the viewBox based on a fraction of the dimension according to the coordinates.
		let paddingFactor = 0.1; //by 10%
		let dW = (boundingBox.xMax - boundingBox.xMin) * paddingFactor;
		let dH = (boundingBox.yMax - boundingBox.yMin) * paddingFactor;

		let xMin: number = Math.max(-halfMapResolution, boundingBox.xMin - dW);
		let xMax: number = Math.min(halfMapResolution, boundingBox.xMax + dW);
		let yMin: number = Math.max(-halfMapResolution, boundingBox.yMin - dH);
		let yMax: number = Math.min(halfMapResolution, boundingBox.yMax + dH);


		let origin: number[] = transform([xMin, yMax]);
		let width: number = xMax - xMin;
		let height: number = yMax - yMin;
		let strokeWidthGain: number = 1; //1/hysplitUtil.Renderer.MAP_RESOLUTION;
		let baseTracerRadius = ((this.options.baseStroke.width ? this.options.baseStroke.width : 1.0) * strokeWidthGain)
		let mainTracerRadius = ((this.options.mainStroke.width ? this.options.mainStroke.width : 1.0) * strokeWidthGain)

		let svg =
			`<svg viewBox="${origin[0]} ${origin[1]} ${width} ${height}" preserveAspectRatio="xMinYMin slice">
                   <defs>
                     <style type="text/css"><![CDATA[
                       g.contour path { ${toCSSContour(this.options.contourFill, this.options.contourStroke, strokeWidthGain)} }
                       g.trajectory path { ${toCSSCurve(this.options.baseStroke, strokeWidthGain)} }
                       g.trajectory circle { ${toCSSFill(this.options.baseStroke.color)} ${toCSSStroke(this.options.baseStroke, strokeWidthGain)} }
                       g.main.trajectory path {  ${toCSSStroke(this.options.mainStroke, strokeWidthGain)} }
                       g.main.trajectory circle { ${toCSSFill(this.options.mainStroke.color)} ${toCSSStroke(this.options.mainStroke, strokeWidthGain)} }`;

		//create style rules for analyzed trajectories
		if (this.options.heightClasses && this.options.heightClasses.thresholds.length > 0) {

			for (let i: number = 0; i <= this.options.heightClasses.thresholds.length; i++) {
				svg += '\n' + getCSSRuleAnalyzedTrajectory(i, this.options.heightClasses, strokeWidthGain, this.options.mainStroke);
			}
		}
		svg += `
              ]]></style>
                   </defs>
              `;
		if (this.options.show.map.contour) {
			svg += Renderer.getPath(this.ensemble.contourPath, 'contour', transform);
		}

		if (this.options.analyze >= this.trajectories.length && this.options.show.map.analyzed) {
			for (let i = 0; i < this.trajectories.length; i++) {
				if (i !== this.options.main) {
					svg += '\n' + this.trajectories[i].latLngCurve.getSVG('trajectory analyzed', transform, baseTracerRadius, (this.options.trace.map.analyzed ? tracerTime : undefined));
				}
			}
			svg += '\n' + this.trajectories[this.options.main].latLngCurve.getSVG('trajectory analyzed main', transform, mainTracerRadius, (this.options.trace.map.analyzed ? tracerTime : undefined));
		} else {
			if (this.options.show.map.alternatives) {
				for (let i = 0; i < this.trajectories.length; i++) {
					i !== this.options.main || !this.options.show.map.main ||
						i !== this.options.analyze || !this.options.show.map.analyzed
					svg += '\n' + this.trajectories[i].latLngCurve.getSVG('trajectory', transform, baseTracerRadius, (this.options.trace.map.alternatives ? tracerTime : undefined));
				}
			}
			if (this.options.analyze !== this.options.main && this.options.analyze < this.trajectories.length) {
				if (this.options.show.map.main) {
					svg += '\n' + this.trajectories[this.options.main].latLngCurve.getSVG('trajectory main', transform, mainTracerRadius, (this.options.trace.map.main ? tracerTime : undefined));
				}
				if (this.options.show.map.analyzed && this.options.analyze != -1) {
					svg += '\n' + this.trajectories[<number>this.options.analyze].latLngCurve.getSVG('trajectory analyzed main', transform, mainTracerRadius, (this.options.trace.map.analyzed ? tracerTime : undefined));
				}
			} else {
				if (this.options.show.map.analyzed && this.options.analyze != -1) {
					svg += '\n' + this.trajectories[<number>this.options.analyze].latLngCurve.getSVG('trajectory analyzed main', transform, mainTracerRadius, (this.options.trace.map.analyzed ? tracerTime : undefined));
				} else if (this.options.show.map.main) {
					svg += '\n' + this.trajectories[this.options.main].latLngCurve.getSVG('trajectory main', transform, mainTracerRadius, (this.options.trace.map.main ? tracerTime : undefined));
				}
			}
		}

		svg += '</svg>';

		//calculate the viewBox in geodesic for map drawing
		let latLng: LatLng;
		latLng = Mercator.fromArrayToLatLng([xMin, yMax], Renderer.MAP_RESOLUTION);
		let north: number = latLng.lat,
			west: number = latLng.lng;
		latLng = Mercator.fromArrayToLatLng([xMax, yMin], Renderer.MAP_RESOLUTION);
		let south: number = latLng.lat,
			east: number = latLng.lng;

		//		return new HysplitLatLngSVG(
		//			svg, {
		//			minX: origin[0],
		//			minY: origin[1],
		//			width: width,
		//			height: height
		//		}, {
		//			lat: south,
		//			lng: west
		//		}, {
		//			lat: north,
		//			lng: east
		//		}
		//		);


		return {
			elem: <SVGElement>markup2element(svg),
			southWest: {
				lat: south,
				lng: west
			},
			northEast: {
				lat: north,
				lng: east
			},
			viewBox: {
				minX: origin[0],
				minY: origin[1],
				width: width,
				height: height
			},
			build: {
				start: 0,
				end: 0,
				time: 0
			}

		}

	}

	public getHeightSVG(mspp: number, targetHeight: number, tracerTime?: number): HysplitHeightSVG {

		//the x axis is the time in millis
		//the y axis is the height in metres and (only) needs to be swapped
		//because of the swapping we need a height at which to swap: the highest of course ;)
		let heightMin;
		let heightMax;
		let stepCountMax;
		for (let i = 0; i < this.trajectories.length; i++) {
			heightMin = heightMin !== undefined ? Math.min(heightMin, this.trajectories[i].heightMin) : this.trajectories[i].heightMin;
			heightMax = heightMax !== undefined ? Math.max(heightMax, this.trajectories[i].heightMax) : this.trajectories[i].heightMax;
			stepCountMax = stepCountMax !== undefined ? Math.max(stepCountMax, this.trajectories[i].heightCurve.getStepCount()) : this.trajectories[i].heightCurve.getStepCount();
		}
		if (!this.options.show.timeline.alternatives) {
			if (this.options.show.timeline.main || (this.options.show.timeline.analyzed && this.options.analyze != -1)) {
				let mainStepCount = this.options.show.timeline.main ? this.trajectories[this.options.main].heightCurve.getStepCount() : 0;
				let analyzedStepCount = this.options.show.timeline.analyzed && this.options.analyze != -1 && this.options.analyze < this.trajectories.length ? this.trajectories[<number>this.options.analyze].heightCurve.getStepCount() : 0;
				stepCountMax = mainStepCount === 0 && analyzedStepCount === 0 ? stepCountMax : Math.max(mainStepCount, analyzedStepCount);
			} else {
				stepCountMax = this.trajectories[this.options.main].heightCurve.getStepCount();
			}
		}
		//take zero for minimum height if heightMin > 0


		//calculate the (initial) viewBox
		//Due to the use of Bezier curves, the exact bounds based on the height values can be too tight.
		//So we widen the viewBox based on a fraction of the height values.
		let heightPaddingFactor = 0.05; //by 5%
		let dH = (heightMax - heightMin) * heightPaddingFactor;

		let tMin: number = this.trajectories[0].heightCurve.getRelativeStartMillis();
		let tMax: number = this.trajectories[0].heightCurve.getRelativeEndMillis();


		let hMin: number = Math.min(heightMin - dH, 0); //set yMin to zero when heightMin minus padding  is not lower then zero. Otherwise take the heightMin minus the padding
		let hMax: number = heightMax + dH;

		let xGain = 1 / mspp; //targetWidth / (tMax - tMin); //should be 1/mspp
		let yGain = targetHeight / (hMax - hMin);

		let transform = (xy: number[]) => [(xy[0] - tMin) * xGain, (hMax - xy[1]) * yGain];
		let origin: number[] = transform([tMin, hMax]);
		let width: number = (tMax - tMin) * xGain;
		let height: number = (hMax - hMin) * yGain;
		let strokeWidthGain: number = 1;
		let baseTracerRadius = ((this.options.baseStroke.width ? this.options.baseStroke.width : 1.0) * strokeWidthGain)
		let mainTracerRadius = ((this.options.mainStroke.width ? this.options.mainStroke.width : 1.0) * strokeWidthGain)

		let svg =
			`<svg viewBox="${origin[0]} ${origin[1]} ${width} ${height}" preserveAspectRatio="none" >
                   <defs>
                     <style type="text/css"><![CDATA[
                       g.trajectory path { ${toCSSCurve(this.options.baseStroke, strokeWidthGain)} }
                       g.trajectory circle { ${toCSSFill(this.options.baseStroke.color)} ${toCSSStroke(this.options.baseStroke, strokeWidthGain)} }
                       g.main.trajectory path {  ${toCSSStroke(this.options.mainStroke, strokeWidthGain)} }
                       g.main.trajectory circle { ${toCSSFill(this.options.mainStroke.color)} ${toCSSStroke(this.options.mainStroke, strokeWidthGain)} }`;

		//create style rules for analyzed trajectories
		if (this.options.heightClasses && this.options.heightClasses.thresholds.length > 0) {
			for (let i: number = 0; i <= this.options.heightClasses.thresholds.length; i++) {
				svg += '\n' + getCSSRuleAnalyzedTrajectory(i, this.options.heightClasses, strokeWidthGain, this.options.mainStroke);
			}
		}
		svg += `
                           ]]></style>
                           </defs>
                           `;


		if (this.options.analyze >= this.trajectories.length && this.options.show.timeline.analyzed) {
			for (let i = 0; i < this.trajectories.length; i++) {
				if (i !== this.options.main) {
					svg += '\n' + this.trajectories[i].heightCurve.getSVG('trajectory analyzed', transform, baseTracerRadius, (this.options.trace.timeline.analyzed ? tracerTime : undefined));
				}
			}
			svg += '\n' + this.trajectories[this.options.main].heightCurve.getSVG('trajectory analyzed main', transform, mainTracerRadius, (this.options.trace.timeline.analyzed ? tracerTime : undefined));
		} else {
			if (this.options.show.timeline.alternatives) {
				for (let i = 0; i < this.trajectories.length; i++) {
					i !== this.options.main || !this.options.show.timeline.main ||
						i !== this.options.analyze || !this.options.show.timeline.analyzed
					svg += '\n' + this.trajectories[i].heightCurve.getSVG('trajectory', transform, baseTracerRadius, (this.options.trace.timeline.alternatives ? tracerTime : undefined));
				}
			}
			if (this.options.analyze !== this.options.main && this.options.analyze < this.trajectories.length) {
				if (this.options.show.timeline.main) {
					svg += '\n' + this.trajectories[this.options.main].heightCurve.getSVG('trajectory main', transform, mainTracerRadius, (this.options.trace.timeline.main ? tracerTime : undefined));
				}
				if (this.options.show.timeline.analyzed && this.options.analyze != -1) {
					svg += '\n' + this.trajectories[<number>this.options.analyze].heightCurve.getSVG('trajectory analyzed main', transform, mainTracerRadius, (this.options.trace.timeline.analyzed ? tracerTime : undefined));
				}
			} else {
				if (this.options.show.timeline.analyzed && this.options.analyze != -1) {
					svg += '\n' + this.trajectories[<number>this.options.analyze].heightCurve.getSVG('trajectory analyzed main', transform, mainTracerRadius, (this.options.trace.timeline.analyzed ? tracerTime : undefined));
				} else if (this.options.show.timeline.main) {
					svg += '\n' + this.trajectories[this.options.main].heightCurve.getSVG('trajectory main', transform, mainTracerRadius, (this.options.trace.timeline.main ? tracerTime : undefined));
				}
			}
		}

		svg += '</svg>';
		return new HysplitHeightSVG(
			svg, {
			minX: origin[0],
			minY: origin[1],
			width: width,
			height: height
		}, {
			start: this.trajectories[0].getDirection() > 0 ? this.trajectories[0].heightCurve.getStartMillis() : (this.trajectories[0].heightCurve.getEndMillis() - (tMax - tMin)),
			end: this.trajectories[0].getDirection() > 0 ? (this.trajectories[0].heightCurve.getStartMillis() + (tMax - tMin)) : this.trajectories[0].heightCurve.getEndMillis(),
			gain: xGain,
			tracer: tracerTime
		}, {
			min: hMin,
			max: hMax,
			gain: yGain,
			tracer: 0 //TODO
		}
		);
	}

}
export interface HysplitProperties {
	id: string,
	result: string,
	direction: string,
	runLength: string,
	startTime: string,
	startHeight: string,
	startLocation: string,
	trajectoryCount: number
}

export interface HysplitOptions {
	main?: number,
	analyze?: number | string,
	contourFill?: SVG.Color,
	contourStroke?: SVG.Stroke,
	mainStroke?: SVG.Stroke,
	baseStroke?: SVG.Stroke,
	heightClasses?: ValueClassification,
	trace?: {
		map?: {
			main?: boolean,
			analyzed?: boolean,
			alternatives?: boolean
		},
		timeline?: {
			main?: boolean,
			analyzed?: boolean,
			alternatives?: boolean
		}
	},
	show?: {
		map?: {
			contour?: boolean,
			main?: boolean,
			analyzed?: boolean,
			alternatives?: boolean
		},
		timeline?: {
			main?: boolean,
			analyzed?: boolean,
			alternatives?: boolean
		}
	}
}

export class HysplitLatLngSVG {
	constructor(public markup: string, public viewBox: SVG.ViewBox, public southWest: {
		lat: number,
		lng: number
	}, public northEast: {
		lat: number,
		lng: number
	}) { }
}
export class HysplitHeightSVG {
	public readonly timeWidth: number;
	constructor(public readonly markup: string, public readonly viewBox: SVG.ViewBox, public readonly time: {
		start: number,
		end: number,
		gain: number,
		tracer?: number
	}, public readonly height: {
		min: number,
		max: number,
		gain: number,
		tracer?: number
	}) {
		this.timeWidth = this.time.end - this.time.start;
	}

	public getViewBoxAttribute(viewPortWidth: number, mspp: number, viewPortStartMillis: number) {
		return [
			this.viewBox.minX + (viewPortStartMillis - this.time.start) * this.time.gain,
			this.viewBox.minY,
			this.viewBox.width * viewPortWidth * mspp / this.timeWidth,
			this.viewBox.height
		].join(" ");
	}
	public isInSync(mspp: number, tracerMillis: number): boolean {
		if (Math.abs(mspp - (1 / this.time.gain)) < 100) { //display mspp is close enough with mspp used for generating this svg in order to keep zooming artifacts to a minimum
			if (isNaN(tracerMillis) && this.time.tracer === undefined) return true; //no tracers needed and no tracers present in svg
			if (!isNaN(tracerMillis) && this.time.tracer !== undefined) {
				return Math.abs(tracerMillis - this.time.tracer) < 1; //allow 1ms difference ;)
			}
		}
		return false;
	}
}

//helpers

interface Threshold {
	value: number,
	stroke: SVG.Stroke
}
export interface ValueClassification {
	thresholds: Threshold[],
	highStroke: SVG.Stroke
}
interface CurveIndex {
	index: number,
	t: number
}
class Renderer {
	public static readonly MAP_RESOLUTION: number = Math.pow(2, 19);

	public static getPath(path: any, classNames: string, transform: (xy: number[]) => number[]) {
		let g = `<g class="${classNames}"><path d="`;
		let p = path[0][0].push ? path : [path]
		let p0, p1, p2, p3;
		for (let j = 0; j < p.length; j++) {
			for (let i = 0; i < p[j].length; i++) {
				if (p[j][i][0] == "C") {
					p1 = transform([p[j][i][1], p[j][i][2]]);
					p2 = transform([p[j][i][3], p[j][i][4]]);
					p3 = transform([p[j][i][5], p[j][i][6]]);
					g += ` C ${p1[0]} ${p1[1]} ${p2[0]} ${p2[1]} ${p3[0]} ${p3[1]}`;
				} else if (p[j][i][0] == "M") {
					p0 = transform([p[j][i][1], p[j][i][2]]);
					g += ` M ${p0[0]} ${p0[1]}`;
				} else if (p[j][i][0] == "Z") {
					g += `Z`;
				} //ignore rest
			}
		}
		g += '" /></g>';
		return g;
	}
}
function toCSSColor(color: SVG.Color, alpha = false): string {
	return alpha ? `hsla(${color.hue},${color.saturation * 100}%,${color.lightness * 100}% ,${color.alpha ? color.alpha : 1.0})` : `hsl(${color.hue},${color.saturation * 100}%,${color.lightness * 100}%)`;
}

function toCSSStroke(stroke: SVG.Stroke, widthGain = 1.0, awStroke?: SVG.Stroke): string {
	return `stroke: ${toCSSColor(stroke.color)}; stroke-opacity: ${awStroke ? awStroke.color.alpha ? awStroke.color.alpha : 1.0 : stroke.color.alpha ? stroke.color.alpha : 1.0}; stroke-width: ${(awStroke ? awStroke.width ? awStroke.width : 1.0 : stroke.width ? stroke.width : 1.0) * widthGain}; ${stroke.dash ? ` stroke-dasharray="${stroke.dash.toString()}";` : ''} stroke-linecap: ${stroke.cap ? stroke.cap : 'butt'};`;
}

function toCSSFill(fill: SVG.Color, aFill?: SVG.Color): string {
	return `fill: ${toCSSColor(fill)}; fill-opacity: ${aFill ? aFill.alpha ? aFill.alpha : 1.0 : fill.alpha ? fill.alpha : 1.0};`;
}

function toCSSCurve(stroke: SVG.Stroke, widthGain = 1.0): string {
	return `fill: none; ${toCSSStroke(stroke, widthGain)}`;
}

function toCSSContour(fill: SVG.Color, stroke: SVG.Stroke, widthGain = 1.0): string {
	return `${toCSSFill(fill)} ${toCSSStroke(stroke, widthGain)}`;
}

function getTrajectoryClassName(classification: number) {
	return 'trajclass_' + classification;
}
function getCSSRuleAnalyzedTrajectory(classification: number, valueClassification: ValueClassification, widthGain: number, mainStroke: SVG.Stroke): string {
	return `g.trajectory.analyzed path.${getTrajectoryClassName(classification)} { ${toCSSStroke((classification < 0 || classification >= valueClassification.thresholds.length) ? valueClassification.highStroke : valueClassification.thresholds[classification].stroke, widthGain)} }
       g.trajectory.analyzed.main path.${getTrajectoryClassName(classification)} { ${toCSSStroke((classification < 0 || classification >= valueClassification.thresholds.length) ? valueClassification.highStroke : valueClassification.thresholds[classification].stroke, widthGain, mainStroke)} }
          g.trajectory.analyzed circle.${getTrajectoryClassName(classification)} { ${(classification < 0 || classification >= valueClassification.thresholds.length) ? toCSSFill(valueClassification.highStroke.color) + ' ' + toCSSStroke(valueClassification.highStroke, widthGain) : toCSSFill(valueClassification.thresholds[classification].stroke.color) + ' ' + toCSSStroke(valueClassification.thresholds[classification].stroke, widthGain)} }
     g.trajectory.analyzed.main circle.${getTrajectoryClassName(classification)} { ${(classification < 0 || classification >= valueClassification.thresholds.length) ? toCSSFill(valueClassification.highStroke.color, mainStroke.color) + ' ' + toCSSStroke(valueClassification.highStroke, widthGain, mainStroke) : toCSSFill(valueClassification.thresholds[classification].stroke.color, mainStroke.color) + ' ' + toCSSStroke(valueClassification.thresholds[classification].stroke, widthGain, mainStroke)} }`;
}
class Trajectory {

	public heightMin: number = undefined;
	public heightMax: number = undefined;
	public timeMin: number = undefined;
	public timeMax: number = undefined;
	public yMax: number = undefined;
	public yMin: number = undefined;
	public xMax: number = undefined;
	public xMin: number = undefined;
	public heightCurve: Curve;
	public latLngCurve: Curve;
	public direction: number;


	constructor(lngLat: number[][], height: number[], T0Millis: number, timestepsMinutes: number[], translate: { dLat: number, dLng: number }, latLngSmoothing = 0.33333333333, heightSmoothing = 0.3) {
		let heightString: number[][] = [];

		let timeSteps = timestepsMinutes.map(minutes => minutes * 60000);

		this.direction = timeSteps[timeSteps.length - 1] - timeSteps[0] < 0 ? -1 : 1;

		for (let i = 0; i < height.length; i++) {
			this.heightMax = this.heightMax !== undefined ? Math.max(height[i], this.heightMax) : height[i];
			this.heightMin = this.heightMin !== undefined ? Math.min(height[i], this.heightMin) : height[i];
			heightString.push([timeSteps[i], height[i]]);
		}
		//Due to the use of Bezier curves, the tight bounds based on the heightMax can be too tight.
		//So we widen the viewBox based on a fraction of the heightMax.
		//let heightPaddingFactor = 0.05; //by 5%
		//heightMax *= (1 + heightPaddingFactor);

		this.heightCurve = new Curve(heightString, T0Millis, timeSteps, heightSmoothing);
		this.timeMin = this.heightCurve.getStartMillis();
		this.timeMax = this.heightCurve.getEndMillis();

		//transform all trajectories from geodetic to  mercator projection
		let latLngString: number[][] = [];

		// when we use coordinates between 0 and 1 only (as given by the initial Mercator projection) we get very strange drawing artifacts when zooming in a lot (like we do when displaying on a map of the local region)
		//so we multiply all coordinates by the number below to get sane drawing when zooming in a lot. 
		//                let magnify = Math.pow(2, 19);
		let latLng: LatLng;
		let point: Point2D;

		for (let i = 0; i < lngLat.length; i++) {
			latLng = new LatLng(lngLat[i][1] - translate.dLat, lngLat[i][0] - translate.dLng);
			point = Mercator.fromLatLngToPoint(latLng, Renderer.MAP_RESOLUTION);
			latLngString.push([point.x, point.y]);

			this.yMax = this.yMax !== undefined ? Math.max(point.y, this.yMax) : point.y;
			this.yMin = this.yMin !== undefined ? Math.min(point.y, this.yMin) : point.y;
			this.xMax = this.xMax !== undefined ? Math.max(point.x, this.xMax) : point.x;
			this.xMin = this.xMin !== undefined ? Math.min(point.x, this.xMin) : point.x;
		}
		this.latLngCurve = new Curve(latLngString, T0Millis, timeSteps, latLngSmoothing)
	}
	public getDirection(): number {
		return this.direction;
	}
	public getTStart(): number {
		return this.direction > 0 ? this.timeMin : this.timeMax;
	}
	public getTEnd(): number {
		return this.direction > 0 ? this.timeMax : this.timeMin;
	}
	public reset(): Trajectory {
		this.heightCurve.reset();
		this.latLngCurve.reset();
		return this;
	}
	public applyValueClassification(valueClassification: ValueClassification) {
		this.reset();

		if (valueClassification) {
			let thresholds: Threshold[] = valueClassification.thresholds;

			let getClassification = function(v: number): number {
				let ti = 0;
				for (; ti < thresholds.length; ti++) {
					if (v <= thresholds[ti].value) {
						return ti;
					}
				}
				return thresholds.length;
			}
			let firstCurveIndex = function(a: CurveIndex, b: CurveIndex): CurveIndex {
				if (a === undefined) return b;
				if (b === undefined) return a;
				if (a.index < b.index) return a;
				if (a.index > b.index) return b;
				if (Math.abs(a.t) < Math.abs(b.t)) return a;
				if (Math.abs(a.t) > Math.abs(b.t)) return b;
				return a;
			}

			let curCurveIndex: CurveIndex = {
				index: 0,
				t: 0
			}
			let section: CubicBezier;
			let newHeightSection: CubicBezier;
			let newLatLngSection: CubicBezier;
			let h: number, classification: number, stroke: SVG.Stroke;
			let nextCurveIndex: CurveIndex = undefined;
			for (let ti = 0; ti < thresholds.length; ti++) {
				nextCurveIndex = firstCurveIndex(this.heightCurve.getCurveIndex(thresholds[ti].value, 1, curCurveIndex), nextCurveIndex);
			}

			while (nextCurveIndex !== undefined) {
				//make a split at nextCurveIndex
				section = this.heightCurve.curveSections[nextCurveIndex.index];
				newHeightSection = section.split(nextCurveIndex.t);
				newLatLngSection = this.latLngCurve.curveSections[nextCurveIndex.index].split(nextCurveIndex.t);
				nextCurveIndex.index++;
				nextCurveIndex.t = 0;
				if (nextCurveIndex.index < this.heightCurve.curveSections.length) {
					this.heightCurve.curveSections.splice(nextCurveIndex.index, 0, newHeightSection);
					this.latLngCurve.curveSections.splice(nextCurveIndex.index, 0, newLatLngSection);
				} else {
					this.heightCurve.curveSections.push(newHeightSection);
					this.latLngCurve.curveSections.push(newLatLngSection);
				}

				//take a height sample from last inspected section and classify
				h = section.get(0.5 * section.dT)[1];
				classification = getClassification(h);

				//apply stroke until new split
				for (let ci = curCurveIndex.index; ci < nextCurveIndex.index; ci++) {
					this.heightCurve.curveSections[ci].classification = classification;
					this.latLngCurve.curveSections[ci].classification = classification;
				}

				curCurveIndex = nextCurveIndex;

				//determine nextCurveIndex
				nextCurveIndex = undefined;
				for (let ti = 0; ti < thresholds.length; ti++) {
					nextCurveIndex = firstCurveIndex(this.heightCurve.getCurveIndex(thresholds[ti].value, 1, curCurveIndex), nextCurveIndex);
				}
			}
			//take a height sample from last inspected section and classify
			section = this.heightCurve.curveSections[this.heightCurve.curveSections.length - 1];
			h = section.get(0.5 * section.dT)[1];
			classification = getClassification(h);

			//apply stroke until new split
			for (let ci = curCurveIndex.index; ci < this.heightCurve.curveSections.length; ci++) {
				this.heightCurve.curveSections[ci].classification = classification;
				this.latLngCurve.curveSections[ci].classification = classification;
			}
		}
	}
}
class Curve {
	private path: (string | number)[][];
	private T0Millis: number;
	private TnMillis: number;
	public curveSections: CubicBezier[];

	constructor(lineString: number[][], startTime: Date | number, public timesteps: number[], smoothing = 0.3333333333) {
		//create a path for our curve based on the lineString
		this.path = Path.smoothenLineString(lineString, smoothing);
		this.T0Millis = (<Date>startTime).getTime ? (<Date>startTime).getTime() : <number>startTime;
		this.TnMillis = this.T0Millis + this.timesteps[this.timesteps.length - 1];
		this.reset();
	}
	/**
	 * This method creates a fresh array of CubicBeziers all with same 
	 * parametric time length. I.e. the initialTimeStep. It uses the 
	 * initial path as a source for control points.
	 */
	public reset(): void {
		if (this.curveSections === undefined ||
			this.path.length !== this.curveSections.length ||
			(this.curveSections.length > 0 && this.curveSections[0].classification !== undefined)) {
			let sections: CubicBezier[] = []
			let p0: number[], p1: number[], p2: number[], p3: number[];
			for (let i = 1; i < this.path.length; i++) {
				p0 = i > 1 ? [<number>this.path[i - 1][5], <number>this.path[i - 1][6]] : [<number>this.path[0][1], <number>this.path[0][2]];
				p1 = [<number>this.path[i][1], <number>this.path[i][2]];
				p2 = [<number>this.path[i][3], <number>this.path[i][4]];
				p3 = [<number>this.path[i][5], <number>this.path[i][6]];
				sections.push(new CubicBezier(p0, p1, p2, p3, this.timesteps[i] - this.timesteps[i - 1]));
			}
			this.curveSections = sections;
		}
	}
	public getStartTime(): Date {
		return new Date(this.getStartMillis());
	}
	public getEndTime(): Date {
		return new Date(this.getEndMillis());
	}
	public getStartMillis(): number {
		return Math.min(this.T0Millis, this.TnMillis);
	}
	public getEndMillis(): number {
		return Math.max(this.T0Millis, this.TnMillis);
	}
	public getRelativeStartMillis(): number {
		return Math.min(this.T0Millis, this.TnMillis) - this.T0Millis;
	}
	public getRelativeEndMillis(): number {
		return Math.max(this.T0Millis, this.TnMillis) - this.T0Millis;
	}
	public getStepCount(): number {
		return this.path.length - 1;
	}
	public getCurveIndex(value: number, axis: number, fromIndex: CurveIndex): CurveIndex {
		let section: CubicBezier;
		let t: number[];
		for (let i: number = (fromIndex.index < 0 ? 0 : fromIndex.index); i < this.curveSections.length; i++) {
			t = this.curveSections[i].inv(value, axis);

			if (t.length > 0) {
				if (i === fromIndex.index) {
					let st = fromIndex.t / this.curveSections[i].dT;
					for (let n = 0; n < t.length; n++) {
						if (greaterThen(t[n], st)) {
							return {
								index: i,
								t: t[n] * this.curveSections[i].dT
							};
						}
					}
				} else {
					return {
						index: i,
						t: t[0] * this.curveSections[i].dT
					};
				}
			}
		}
		return undefined;
	}
	private getCurveIndexForTime(timeMillis: number): CurveIndex {
		if (timeMillis >= Math.min(this.T0Millis, this.TnMillis) && timeMillis <= Math.max(this.T0Millis, this.TnMillis)) {
			let t = timeMillis - this.T0Millis;
			let travelTime = 0;
			let test = this.T0Millis < this.TnMillis ? (i: number) => travelTime + this.curveSections[i].dT > t : (i: number) => travelTime + this.curveSections[i].dT < t;
			for (let i = 0; i < this.curveSections.length; i++) {
				if (test(i)) {
					return {
						index: i,
						t: t - travelTime
					};
				} else {
					travelTime += this.curveSections[i].dT;
				}
			}
		}
		return undefined;
	}
	public getSVG(classNames: string, transform: (xy: number[]) => number[], tracerRadius: number, tracerTime?: number): string {
		if (this.curveSections && this.curveSections.length > 0) {
			let classification: number = this.curveSections[0].classification;
			let p0: number[] = transform(this.curveSections[0].p0);
			let g = `<g class="${classNames}"><path class="${classification !== undefined ? getTrajectoryClassName(classification) : ''}"  d="M ${p0[0]} ${p0[1]}`;
			let p1: number[], p2: number[], p3: number[];
			for (let i = 0; i < this.curveSections.length; i++) {
				if (i > 0 && classification != this.curveSections[i].classification) {
					classification = this.curveSections[i].classification;
					p0 = transform(this.curveSections[i].p0);
					g += `" /><path class="${classification !== undefined ? getTrajectoryClassName(classification) : ''}"  d="M ${p0[0]} ${p0[1]}`;
				}
				p1 = transform(this.curveSections[i].p1);
				p2 = transform(this.curveSections[i].p2);
				p3 = transform(this.curveSections[i].p3);
				g += ` C ${p1[0]} ${p1[1]} ${p2[0]} ${p2[1]} ${p3[0]} ${p3[1]}`;
			}
			g += '" />';
			if (!isNaN(tracerTime)) {
				let tracerIndex: CurveIndex = this.getCurveIndexForTime(tracerTime);
				if (tracerIndex) {
					let tracerClassification = this.curveSections[tracerIndex.index].classification;
					let pTracer = transform(this.curveSections[tracerIndex.index].get(tracerIndex.t));

					g += `<circle class="${classification !== undefined ? getTrajectoryClassName(tracerClassification) : ''}" cx="${pTracer[0]}" cy="${pTracer[1]}" r="${tracerRadius}"/>`;
				}
			}
			g += '</g>';
			return g;
		}
		return '';
	}
}
class Ensemble {

	public yMax: number = undefined;
	public yMin: number = undefined;
	public xMax: number = undefined;
	public xMin: number = undefined;
	public contourPath: (string | number)[][];

	constructor(lngLatLines: number[][][], startWidth = 150, endWidth = 1000, translate: { dLat: number, dLng: number }, contourSmoothing = 0.33333333333) {
		//        let datum: Datum = TransverseMercator.getDefaultDatum().origin(new LatLng(lngLatLines[0][0][1], lngLatLines[0][0][0]));
		let datum: Datum = TransverseMercator.getDefaultDatum().origin(new LatLng(lngLatLines[0][0][1] - translate.dLat, lngLatLines[0][0][0] - translate.dLng));
		let xy: number[][], xyLines: number[][][] = [];
		let latLng: LatLng;
		let point: Point2D;

		//transform all coordinates from geodetic to  cartesian
		for (let i = 0; i < lngLatLines.length; i++) {
			xy = [];
			xyLines.push(xy);
			for (let j = 0; j < lngLatLines[i].length; j++) {
				latLng = new LatLng(lngLatLines[i][j][1] - translate.dLat, lngLatLines[i][j][0] - translate.dLng);
				point = datum.direct(latLng);
				xy.push([point.x, point.y]);
			}
		}

		// culculate contour
		let contour: number[][] = new MultiLineString(xyLines, 1.0).getContour(startWidth, endWidth);
		if (contour) {
			//            contour = Path.resizeContour(contour, contourGrowth);

			//transform contour from cartesian to mercator projection
			for (let i = 0; i < contour.length; i++) {
				point = new Point2D(contour[i][0], contour[i][1]);
				latLng = datum.inverse(point);
				point = Mercator.fromLatLngToPoint(latLng, Renderer.MAP_RESOLUTION);
				contour[i] = [point.x, point.y];

				this.yMax = this.yMax !== undefined ? Math.max(point.y, this.yMax) : point.y;
				this.yMin = this.yMin !== undefined ? Math.min(point.y, this.yMin) : point.y;
				this.xMax = this.xMax !== undefined ? Math.max(point.x, this.xMax) : point.x;
				this.xMin = this.xMin !== undefined ? Math.min(point.x, this.xMin) : point.x;
			}
			this.contourPath = Path.smoothenContour(contour, contourSmoothing)
		} else {
			this.contourPath = undefined;
		}
	}
}
/**
 * An ensemble of lines.
 */
export class MultiLineString {
	//    public lines: LineString[]; //the lines  that make up this ensemble
	//    private startCapLines: LineString[]; //helper lines needed for finding the contour of the ensemble, by connecting all LineString beginnings
	//    private endCapLines: LineString[]; //helper lines needed for finding the contour of the ensemble, by connecting all LineString endings 
	//    private xMax: number[]; //the index number of the line, its segment the end point that has the maximum x coordinate
	//    private startPoint: number[];


	private lineStrings: number[][] = [];//the lines  that make up this ensemble
	private legs: { lineStrings: number[], segments: Line[] }[] = []; //the individual straight lines between the initial points.
	private xMaxValue: number; // the greatest x value
	private xMax: number[] = null;//the index number of the line leg segment and  end point that has the maximum x coordinate
	private startPoint: number[]; //the start point of the ensmble


	/**
	 * Constructs an ensemble of lineStrings with the lines given.
	 * @param multiLineString the coordinates that make up the linesStrings that make up the ensemble
	 * @param lineDT the time it takes to travel from point to point
	 */
	constructor(multiLineString: number[][][], lineDT: number) {
		let iLegExisting: number;
		let lineString: number[];
		let segment: Line;
		let dT: number;
		let p0: number[];
		let p1: number[];

		this.startPoint = multiLineString.length > 0 ? multiLineString[0][0] : null;
		for (let iLine = 0; iLine < multiLineString.length; iLine++) {
			lineString = [];
			this.lineStrings.push(lineString);
			for (let iLeg = 0; iLeg < multiLineString[iLine].length - 1; iLeg++) {
				p0 = multiLineString[iLine][iLeg];
				p1 = multiLineString[iLine][iLeg + 1];
				if (p0[0] != p1[0] || p0[1] != p1[1]) {
					iLegExisting = this.legs.findIndex((leg) => leg.segments[0].hasEndPoints(multiLineString[iLine][iLeg], multiLineString[iLine][iLeg + 1], true) != 0);
					if (iLegExisting == -1) {
						iLegExisting = this.legs.length;
						segment = new Line(
							multiLineString[iLine][iLeg][0],
							multiLineString[iLine][iLeg][1],
							multiLineString[iLine][iLeg + 1][0],
							multiLineString[iLine][iLeg + 1][1],
							lineDT,
							'' + iLine + '_' + iLeg);
						this.legs.push({ lineStrings: [iLine], segments: [segment] });
						if (this.xMaxValue == null || this.xMaxValue < segment.getXMax()) {
							this.xMaxValue = segment.getXMax();
							this.xMax = [iLine, iLeg, 0, segment.xMax];
						}
					} else {
						this.legs[iLegExisting].lineStrings.push(iLine);
					}
					lineString.push(iLegExisting);
					dT = lineDT;
				} else {
					dT += lineDT;
				}
			}
		}
	}

	private getInflatedLine(lineIndex: number, startWidth: number, endWidth: number): Polygon {
		return Path.inflateLineString(this.getLineString(lineIndex), startWidth, endWidth);
	}
	private getLineString(index: number): [number, number][] {
		let lineString: [number, number][] = [];
		let segments = this.legs[this.lineStrings[index][0]].segments;
		lineString.push(<[number, number]>segments[0].c[0]);
		for (let i = 0; i < this.lineStrings[index].length; i++) {
			segments = this.legs[this.lineStrings[index][i]].segments;
			for (let j = 0; j < segments.length; j++) {
				lineString.push(<[number, number]>segments[j].c[1]);
			}
		}
		return lineString;
	}

	public getContour(startWidth: number, endWidth: number): [number, number][] {
		try {
			let inflatedLines = this.lineStrings.map((_, index) => {
//				console.log("GET INFLATED LINE "+index); 
				return this.getInflatedLine(index, startWidth, endWidth) });
				
//				console.info('[\n'+inflatedLines.map(polygon => polygon.toString()).join(',\n')+'\n]');
			
			    const setSweeper = new SetSweeper(inflatedLines);
                const union = setSweeper.getUnion();

//			let setBuilder = new SetBuilder(inflatedLines);
//			let union = setBuilder.getUnion();
			
			return union.getPathVertices(0);
			
		} catch (error) {
			console.error("Contour construction failed.", error);
		}
		return null;
	}

	/**
	 * Returns the contour that runs around the ensemble
	 */
	public getContour2(): number[][] {
		let neighbourhoods = this.getNeighbourhoods();
		let capLegs = this.getCapSegments(neighbourhoods);

		let allLegs = capLegs.concat(this.legs);
		let legs = <{ lineStrings: number[], segments: Line[] }[]>this.getIntersected(allLegs);
		let contour = this.calculateContour(legs);

		return contour;
	}

	private getNeighbourhoods() {
		let allNeighbourhoods: number[][] = this.lineStrings.map((lineString: number[], index: number) => this.findEndPointNeighbourhood(index).inside);

		let neighbourhoods = allNeighbourhoods.reduce((neighbourhoods, neighbourhood) => {
			if (neighbourhoods.length == 0) {
				return [neighbourhood];
			} else {
				let noneIntersectingNeighbourhoods = neighbourhoods.filter(nh => neighbourhood.filter(line => nh.indexOf(line) != -1).length == 0);
				//find neighbourhoods that intersects and merge them all together. Should be only one but if we find more we merge them

				let newNeighbourhood: number[] = neighbourhoods
					.filter(nh => neighbourhood.filter(line => nh.indexOf(line) != -1).length > 0) //these neighbourhoods intersect
					.reduce((bigNeighbourHood, smallNeighbourhood) => {
						smallNeighbourhood.filter(line => bigNeighbourHood.indexOf(line) == -1).forEach(line => bigNeighbourHood.push(line));
						return bigNeighbourHood;
					}
						, <number[]>[])
					.reduce((bigNeighbourHood, line) => {
						if (bigNeighbourHood.indexOf(line) == -1) bigNeighbourHood.push(line);
						return bigNeighbourHood;
					}
						, neighbourhood);

				noneIntersectingNeighbourhoods.push(newNeighbourhood);
				return noneIntersectingNeighbourhoods;
			}
		}, <number[][]>[]);
		return neighbourhoods;
	}
	private findEndPointNeighbourhood(lineStringIndex: number): { inside: number[], outside: number[], radius: number, center: number[] } {
		//define some helper functions
		let getLastLeg = (i: number) => this.legs[this.lineStrings[i][this.lineStrings[i].length - 1]];
		let getEndPoint = (leg: { lineStrings: number[], segments: Line[] }) => leg.segments[leg.segments.length - 1].c[1];
		let dist = (p0: number[], p1: number[]) => Math.sqrt((p0[0] - p1[0]) * (p0[0] - p1[0]) + (p0[1] - p1[1]) * (p0[1] - p1[1]));
		let pointsEqual = (p0: number[], p1: number[]) => p0 && p1 && p0[0] == p1[0] && p0[1] == p1[1];


		let endPoints = this.lineStrings.map((lineString, index) => getEndPoint(getLastLeg(index)));
		let endPoint = getEndPoint(getLastLeg(lineStringIndex));

		//create an array of legs sorted by their distance from their end point and the end point of the line we are determining the neighbourhood for
		let legs = this.legs.concat([]).sort((leg0, leg1) => dist(getEndPoint(leg0), endPoint) - dist(getEndPoint(leg1), endPoint));

		let neighbourhood: { inside: number[], outside: number[], radius: number, center: number[] } = { inside: [], outside: [], radius: 0, center: endPoint };
		let leg;
		let lines;
		let p;
		let found = false;
		let outIndex;
		//                let potentialNewNeighbours;
		for (let iLeg = 0; iLeg < legs.length && !found; iLeg++) {
			leg = legs[iLeg];
			lines = leg.lineStrings;
			p = getEndPoint(leg);
			//                  potentialNewNeighbours = [];
			for (let j = 0; j < lines.length; j++) {
				if (neighbourhood.inside.indexOf(lines[j]) == -1) {
					if (pointsEqual(p, endPoints[lines[j]])) {
						neighbourhood.inside.push(lines[j]);
						neighbourhood.inside.sort((a, b) => a - b);
						outIndex = neighbourhood.outside.indexOf(lines[j]);
						if (outIndex != -1) {
							neighbourhood.outside.splice(outIndex, 1);
						}
					} else {
						if (neighbourhood.outside.indexOf(lines[j]) == -1) {
							neighbourhood.outside.push(lines[j]);
							neighbourhood.outside.sort((a, b) => a - b);
						}
					}
				}
			}
			//check if we visited all lines
			found = (neighbourhood.inside.length + neighbourhood.outside.length) == this.lineStrings.length;

			//              if(!found){
			//                  for(let j=0; j<potentialNewNeighbours.length; j++) neighbourhood.inside.push(potentialNewNeighbours[j]);
			//                  neighbourhood.radius = dist( p, endPoint )
			//              }
		}
		//console.log("NEIGHBOURHOOD FOR LINE %s = %s %o %",lineStringIndex, neighbourhood.inside, neighbourhood);
		return neighbourhood;
	}
	private getCapSegments(neighbourhoods: number[][]): { lineStrings: number[], segments: Line[] }[] {
		let capLegs: { lineStrings: number[], segments: Line[] }[] = []
		for (let i = 0; i < neighbourhoods.length; i++) {
			capLegs = capLegs.concat(this.getLocalCapSegments(neighbourhoods[i]));
		}
		return capLegs;
	}
	private getLocalCapSegments(lineCollection: number[]) {

		let getEndPoint = (i: number) => {
			let lineString = this.lineStrings[i];
			let lastLeg = this.legs[lineString[lineString.length - 1]];
			let lastSegment = lastLeg.segments[lastLeg.segments.length - 1];
			return lastSegment.c[1];
		};
		let pointsEqual = (p0: number[], p1: number[]) => {
			return p0 && p1 && p0[0] == p1[0] && p0[1] == p1[1];
		};
		let isConnectingLeg = (leg: { lineStrings: number[], segments: Line[] }, lineIndex0: number, lineIndex1: number) => {
			return leg.lineStrings.indexOf(lineIndex0) != -1 && leg.lineStrings.indexOf(lineIndex1) != -1;
		}

		let capLegs = [];
		let intersections: number[][][][] | number[][][][][];
		//interconnect all end-point
		for (let i = 0; i < lineCollection.length; i++) {
			let endPoint = getEndPoint(lineCollection[i]);
			for (let j = 0; j < lineCollection.length; j++) {
				let otherEndPoint = getEndPoint(lineCollection[j]);
				if (!pointsEqual(endPoint, otherEndPoint)) {//points are not equal
					if (capLegs.findIndex(leg => isConnectingLeg(leg, lineCollection[i], lineCollection[j])) == -1) {//no connection between these lines exist
						let connectingLeg = { lineStrings: [lineCollection[i], lineCollection[j]], segments: [new Line(endPoint[0], endPoint[1], otherEndPoint[0], otherEndPoint[1], 1.0, 'cap_' + lineCollection[i] + '_' + lineCollection[j])] };
						capLegs.push(connectingLeg);
					}
				}
			}
		}

		//remove all legs that intersect a line string
		intersections = this.findIntersections(capLegs, this.legs);
		capLegs = capLegs.filter((leg, legIndex) => intersections[0][legIndex][0].length == 0);

		//remove all legs that intersect each other
		intersections = this.findIntersections(capLegs);
		capLegs = capLegs.filter((leg, legIndex) => intersections[legIndex][0].length == 0);

		//now find any line string that has no connecting leg with another line string and find an alternative connection (with findSegment)  

		return capLegs;
	}

	private findIntersections(legs0: { lineStrings: number[], segments: Line[] }[], legs1?: { lineStrings: number[], segments: Line[] }[]): number[][][][] | number[][][][][] {
		let addIntersection = (intersections: number[][], intersection: number[], pStart: number[]) => {
			if (intersection) {
				if (!intersections) {
					intersections = [intersection];
				} else if (intersections.length == 0) {
					intersections.push(intersection);
				} else {
					if (intersections.findIndex(inter => inter[0] === intersection[0] && inter[1] === intersection[1]) == -1) {
						intersections.push(intersection);
						intersections.sort((p0, p1) => {
							let xL0 = p0[0] - pStart[0];
							let yL0 = p0[1] - pStart[1];
							let l0 = Math.sqrt(xL0 * xL0 + yL0 * yL0);
							let xL1 = p1[0] - pStart[0];
							let yL1 = p1[1] - pStart[1];
							let l1 = Math.sqrt(xL1 * xL1 + yL1 * yL1);
							return l0 - l1;
						});
					}
				}
			}
		}

		let setsAreEqual = legs1 === undefined;
		if (legs1 === undefined) {
			legs1 = legs0;
		}

		let intersections0: number[][][][] = [];
		let intersections1: number[][][][] = [];
		let count = 0;
		let intersection: number[]

		for (let iLeg0 = 0; iLeg0 < legs0.length; iLeg0++) {
			if (intersections0.length == iLeg0) intersections0.push([]);
			for (let iSegment0 = 0; iSegment0 < legs0[iLeg0].segments.length; iSegment0++) {
				if (intersections0[iLeg0].length == iSegment0) intersections0[iLeg0].push([]);
				for (let iLeg1 = 0; iLeg1 < legs1.length; iLeg1++) {
					if (intersections1.length == iLeg1) intersections1.push([]);
					for (let iSegment1 = 0; iSegment1 < legs1[iLeg1].segments.length; iSegment1++) {
						if (intersections1[iLeg1].length == iSegment1) intersections1[iLeg1].push([]);
						intersection = legs0[iLeg0].segments[iSegment0].intersection(legs1[iLeg1].segments[iSegment1]);
						if (intersection) {
							count++;
							addIntersection(intersections0[iLeg0][iSegment0], intersection, legs0[iLeg0].segments[iSegment0].c[0]);
							addIntersection(intersections1[iLeg1][iSegment1], intersection, legs1[iLeg1].segments[iSegment1].c[0]);
						}
					}
				}
			}
		}
		//console.log('%s intersections found',count);
		if (setsAreEqual) {
			return intersections0;
		} else {
			return [intersections0, intersections1];
		}

	}

	private getIntersected(legs0: { lineStrings: number[], segments: Line[] }[], legs1?: { lineStrings: number[], segments: Line[] }[]): { lineStrings: number[], segments: Line[] }[] | { lineStrings: number[], segments: Line[] }[][] {
		let intersections: number[][][][] | number[][][][][] = this.findIntersections(legs0, legs1);
		//flat and flatMap are not supported by all browsers (Edge, of course) so use a reduce step instead

		if (legs1 === undefined) {
			let intersectedLegs = legs0.map((leg, iLeg) => { return { lineStrings: [].concat(leg.lineStrings), segments: leg.segments.map((segment, iSegment) => segment.getSplits((<number[][][][]>intersections)[iLeg][iSegment])).reduce((accu, splits) => accu.concat(splits), []) }; });
			return intersectedLegs;
		} else {
			let intersectedLegs0 = legs0.map((leg, iLeg) => { return { lineStrings: [].concat(leg.lineStrings), segments: leg.segments.map((segment, iSegment) => segment.getSplits((<number[][][][][]>intersections)[0][iLeg][iSegment])).reduce((accu, splits) => accu.concat(splits), []) }; });
			let intersectedLegs1 = legs1.map((leg, iLeg) => { return { lineStrings: [].concat(leg.lineStrings), segments: leg.segments.map((segment, iSegment) => segment.getSplits((<number[][][][][]>intersections)[1][iLeg][iSegment])).reduce((accu, splits) => accu.concat(splits), []) }; });
			return [intersectedLegs0, intersectedLegs1];
		}
	}

	private calculateContour(legs: { lineStrings: number[], segments: Line[] }[], maxIteration?: number): number[][] {
		maxIteration = maxIteration ? maxIteration : 2000;
		let startPointIndex = undefined;
		let i, contour, nextSegment: Line;
		let allSegments = legs.reduce((segments, leg) => segments.concat(leg.segments), []);
		let start = this.legs[this.lineStrings[this.xMax[0]][this.xMax[1]]].segments[this.xMax[2]].c[this.xMax[3]];

		let point = start;
		let atAngle = 0;
		let nextSegments = allSegments.filter(segment => segment.getIndex(point) >= 0).sort((sa, sb) => {
			let angleDiff = sa.getAngle(point, atAngle) - sb.getAngle(point, atAngle);
			return angleDiff
		});

		if (nextSegments.length > 0) {
			contour = [
				[point[0], point[1]]
			];
			if (startPointIndex === undefined && closePoints(point, this.startPoint)) {
				startPointIndex = contour.length - 1;
			}
			nextSegment = nextSegments[nextSegments.length - 1];
			atAngle = nextSegment.getInverseAngle(point);
			point = nextSegment.c[1 - nextSegment.getIndex(point)];

			i = 0;
			while ((point[0] != start[0] || point[1] != start[1]) && i < maxIteration) {
				if (i == (maxIteration - 1)) {
					console.log('Reached maximum number of iterations.');
				}
				//console.info(point);
				contour.push([point[0], point[1]]);
				if (startPointIndex === undefined && closePoints(point, this.startPoint)) {
					startPointIndex = contour.length - 1;
				}
				nextSegments = allSegments.filter(segment => segment.getIndex(point) >= 0 && !segment.equals(nextSegment)).sort((sa, sb) => {
					let angleDiff = sa.getAngle(point, atAngle) - sb.getAngle(point, atAngle);
					return angleDiff
				});
				if (nextSegments.length > 0) {
					nextSegment = nextSegments[nextSegments.length - 1];
					atAngle = nextSegment.getInverseAngle(point);
					point = nextSegment.c[1 - nextSegment.getIndex(point)];
					i++;
				} else {
					console.error("Could not find next contour segment.");
					i = maxIteration;
				}
			}
			if (i >= maxIteration) {
				//                  console.error("Could not find next contour segment. Contour will be auto closed with %i points so far.", (contour.length));
				console.error("Could not find contour.");
				return undefined;
			} else {
				//console.log("Contour found (%i points)", (contour.length));
			}
			if (startPointIndex > 0) {
				contour = contour.concat(contour.slice(0, startPointIndex));
				contour.splice(0, startPointIndex);
			}
			return contour;
		} else {
			console.error("Could not find contour.");
			return undefined;
		}
	}

}

